import React, { useEffect, useState } from 'react';
import { LogOut, ShieldAlert, Clock, Plus } from 'lucide-react';

export interface ImpersonationBannerProps {
  /** Whether an impersonation session is active. If false, component renders nothing. */
  active: boolean;
  /** Display name of the impersonated user. */
  displayName?: string | null;
  /** Optional secondary identifier (typically email). */
  displayEmail?: string | null;
  /** Absolute date when the session expires. Used for the countdown. Optional. */
  expiresAt?: Date | null;
  /** Handler invoked when the user clicks "Exit impersonation". */
  onExit: () => void;
  /**
   * Optional handler to extend the session by 30 min. When provided, a "+30 min" button
   * appears inside the banner once the remaining time drops below 5 minutes.
   */
  onExtend?: () => void | Promise<void>;
  /** Disables the extend button (e.g. during the request). */
  isExtending?: boolean;
}

function formatTimeLeft(ms: number): string {
  if (ms <= 0) return 'expired';
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes <= 0) return `${seconds}s`;
  if (minutes < 60) return `${minutes}m ${seconds.toString().padStart(2, '0')}s`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours}h ${mins.toString().padStart(2, '0')}m`;
}

export const ImpersonationBanner: React.FC<ImpersonationBannerProps> = ({
  active,
  displayName,
  displayEmail,
  expiresAt,
  onExit,
  onExtend,
  isExtending,
}) => {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!active || !expiresAt) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [active, expiresAt]);

  if (!active) return null;

  const name = displayName || 'this account';
  const timeLeftMs = expiresAt ? expiresAt.getTime() - now : null;
  const isAboutToExpire = timeLeftMs !== null && timeLeftMs <= 5 * 60 * 1000;
  const bgClass = isAboutToExpire ? 'bg-red-600' : 'bg-amber-500';
  const showExtend = onExtend && timeLeftMs !== null && timeLeftMs <= 5 * 60 * 1000;

  return (
    <div className={`sticky top-0 z-[9999] ${bgClass} text-white shadow-md`}>
      <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2 text-sm font-medium min-w-0">
          <ShieldAlert className="w-5 h-5 flex-shrink-0" />
          <span className="truncate">
            You are impersonating <strong>{name}</strong>
            {displayEmail ? ` (${displayEmail})` : ''}. All actions are audited.
          </span>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {timeLeftMs !== null && (
            <span className="inline-flex items-center gap-1 text-xs font-semibold bg-white/20 px-2 py-1 rounded">
              <Clock className="w-3.5 h-3.5" />
              {formatTimeLeft(timeLeftMs)}
            </span>
          )}
          {showExtend && (
            <button
              onClick={onExtend}
              disabled={isExtending}
              className="inline-flex items-center gap-1 bg-white/20 hover:bg-white/30 text-white px-2.5 py-1 rounded-md text-xs font-semibold transition-colors disabled:opacity-60"
              title="Extend session by 30 minutes"
            >
              <Plus className="w-3.5 h-3.5" />
              {isExtending ? 'Extending…' : '30 min'}
            </button>
          )}
          <button
            onClick={onExit}
            className="inline-flex items-center gap-1 bg-white text-amber-700 hover:bg-amber-50 px-3 py-1 rounded-md text-sm font-semibold transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Exit
          </button>
        </div>
      </div>
    </div>
  );
};
